import { z } from 'zod';
import { ComponentTypeSchema } from './site-report.js';
/**
 * Finding — prienik Catalogu a Inventory (CONTEXT.md). Nie je to Vulnerability:
 * má siteId, životný cyklus a prežíva rescan.
 *
 * Závažnosť je odvodená z `cvssRating` katalógového záznamu; záznam bez CVSS
 * (spravidla scanner-only) je `unconfirmed` — NEPOTVRDENÝ, nie neškodný.
 * Poradie enumu = poradie od najhoršieho; UI ho vždy zobrazuje s popiskom.
 */
export const SeveritySchema = z.enum(['critical', 'high', 'medium', 'low', 'unconfirmed']);
export const SEVERITY_ORDER = SeveritySchema.options;
export const FindingStatusSchema = z.enum(['OPEN', 'RESOLVED', 'IGNORED', 'ACCEPTED_RISK']);
/** Prečo sa nález uzavrel: opravené, komponent odstránený, alebo záznam stiahnutý z katalógu. */
export const ResolvedReasonSchema = z.enum(['PATCHED', 'REMOVED', 'WITHDRAWN']);
/**
 * Riadok nálezu tak, ako ho vidí dashboard — detail stránky, prehľad aj
 * (ticket 09) tabuľka naprieč stránkami s filtrami. Ticket 09 schému rozširuje,
 * nemení: filtre (severity, status, type, site, patch) sedia na tieto polia.
 */
export const FindingSchema = z.object({
    id: z.string(),
    siteId: z.string(),
    vulnId: z.string(),
    status: FindingStatusSchema,
    resolvedReason: ResolvedReasonSchema.nullable(),
    severity: SeveritySchema,
    title: z.string(),
    cve: z.string().nullable(),
    cvss: z.number().nullable(),
    /** nainštalovaná verzia v čase posledného matchu */
    installedVersion: z.string(),
    /** najbližšia opravená verzia väčšia než nainštalovaná; null = oprava (zatiaľ) neexistuje */
    patchedIn: z.string().nullable(),
    firstDetectedAt: z.string(),
    lastSeenAt: z.string(),
    resolvedAt: z.string().nullable(),
    site: z.object({ id: z.string(), name: z.string(), host: z.string() }),
    component: z.object({ type: ComponentTypeSchema, slug: z.string(), name: z.string() }),
});
export const SeverityCountsSchema = z.object({
    critical: z.number().int(),
    high: z.number().int(),
    medium: z.number().int(),
    low: z.number().int(),
    unconfirmed: z.number().int(),
});
/**
 * Prehľad. Dve rôzne „aktuálnosti" sú zámerne oddelené: `catalog` hovorí o
 * FeedSyncRun (kedy sme naposledy stiahli Wordfence), `scans` o ScanRun
 * (kedy sa naposledy ozvali stránky). „Katalóg starý 4 h" a „stránky skenované
 * pred 10 min" nie je to isté.
 */
export const DashboardSchema = z.object({
    sites: z.object({
        total: z.number().int(),
        /** úspešný report za posledných 24 h */
        reportedToday: z.number().int(),
        /** stránky, ktoré sa neozvali (error) alebo ešte nereportovali — ich „0 nálezov" nič neznamená */
        unknown: z.number().int(),
    }),
    findings: z.object({
        open: z.number().int(),
        newLast24h: z.number().int(),
        critical: z.number().int(),
        criticalSites: z.number().int(),
        /** otvorené nálezy, pre ktoré už existuje opravená verzia */
        fixable: z.number().int(),
    }),
    bySeverity: SeverityCountsSchema,
    recent: z.array(FindingSchema),
    catalog: z.object({
        total: z.number().int(),
        /** posledný úspešný FeedSyncRun (ktorýkoľvek feed) */
        lastSyncAt: z.string().nullable(),
        lastProductionSyncAt: z.string().nullable(),
    }),
    scans: z.object({
        /** posledný úspešný report ktorejkoľvek stránky */
        lastReportAt: z.string().nullable(),
        /** posledný ScanRun bez ohľadu na výsledok */
        lastRunAt: z.string().nullable(),
    }),
});
/* ---------- Ticket 09: tabuľka naprieč stránkami ---------- */
/**
 * Zoznam hodnôt v URL: `severity=critical,high` (jeden parameter, čiarky) aj
 * `severity=critical&severity=high` (opakovaný parameter) sa prijmú rovnako.
 * Serializuje sa vždy do tvaru s čiarkami — kratší, čitateľnejší odkaz.
 */
function listOf(item) {
    return z.preprocess((v) => (typeof v === 'string' ? v.split(',').map((s) => s.trim()).filter(Boolean) : Array.isArray(v) ? v.flatMap((x) => (typeof x === 'string' ? x.split(',') : [x])) : v), z.array(item));
}
export const FindingsSortSchema = z.enum(['severity', 'detected', 'site', 'component', 'cvss']);
export const FINDINGS_PAGE_SIZE_DEFAULT = 25;
export const FINDINGS_PAGE_SIZE_MAX = 100;
/** Bez `status` v URL sa ukazujú otvorené nálezy — to, čo riešim práve teraz. */
export const FINDINGS_STATUS_DEFAULT = ['OPEN'];
/**
 * Tvar filtrov tabuľky nálezov — JEDNA schéma pre URL (`useSearchParams`) aj
 * query string API. Vstup je vždy „stringový" (URL), výstup typovaný; backend
 * pri nezmysle vráti 400, frontend sa vráti k predvoleným hodnotám.
 * Neznáme parametre sa ignorujú (odkaz s `utm_*` nemá padnúť).
 */
export const FindingsQuerySchema = z.object({
    siteId: z.uuid().optional(),
    severity: listOf(SeveritySchema).optional(),
    type: listOf(ComponentTypeSchema).optional(),
    status: listOf(FindingStatusSchema).default([...FINDINGS_STATUS_DEFAULT]),
    /** true = len nálezy so známou opravenou verziou, false = len bez nej */
    patchAvailable: z.stringbool().optional(),
    /** fulltext nad názvom zraniteľnosti, CVE, slugom a názvom komponentu */
    q: z.string().trim().max(200).transform((s) => (s === '' ? undefined : s)).optional(),
    sort: FindingsSortSchema.default('severity'),
    dir: z.enum(['asc', 'desc']).default('asc'),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(FINDINGS_PAGE_SIZE_MAX).default(FINDINGS_PAGE_SIZE_DEFAULT),
});
/** Jedna stránka tabuľky; `total` je počet riadkov po filtri, nie v katalógu. */
export const FindingsPageSchema = z.object({
    items: z.array(FindingSchema),
    total: z.number().int(),
    page: z.number().int(),
    pageSize: z.number().int(),
    /** počty podľa závažnosti nad tým istým filtrom BEZ filtra závažnosti — na čipy s číslami */
    bySeverity: SeverityCountsSchema,
});
/**
 * Atribúcia MITRE/Defiant — licenčná povinnosť (Wordfence Intelligence terms,
 * CVE® terms of use). Nesie sa pri každom zázname a musí byť viditeľná v UI aj v exporte.
 */
export const CopyrightEntrySchema = z.object({
    notice: z.string(),
    license: z.string(),
    licenseUrl: z.string(),
});
export const VersionRangeSchema = z.object({
    label: z.string(),
    fromVersion: z.string(),
    fromInclusive: z.boolean(),
    toVersion: z.string(),
    toInclusive: z.boolean(),
});
export const VulnerableSoftwareSchema = z.object({
    type: ComponentTypeSchema,
    slug: z.string(),
    name: z.string(),
    patched: z.boolean(),
    remediation: z.string().nullable(),
    ranges: z.array(VersionRangeSchema),
    patchedVersions: z.array(z.string()),
});
/** Katalógový záznam (Vulnerability) tak, ako ho ukazuje detail — globálny, bez siteId. */
export const VulnerabilityDetailSchema = z.object({
    id: z.string(),
    title: z.string(),
    description: z.string().nullable(),
    informational: z.boolean(),
    severity: SeveritySchema,
    cve: z.string().nullable(),
    cveLink: z.string().nullable(),
    cvss: z.object({ score: z.number().nullable(), vector: z.string().nullable(), rating: z.string().nullable() }).nullable(),
    cwe: z.object({ id: z.number().int(), name: z.string().nullable() }).nullable(),
    references: z.array(z.string()),
    researchers: z.array(z.string()),
    published: z.string().nullable(),
    updated: z.string().nullable(),
    software: z.array(VulnerableSoftwareSchema),
    copyrights: z.object({ message: z.string().nullable(), entries: z.array(CopyrightEntrySchema) }),
    /** z ktorých feedov záznam pochádza (scanner-only = bez CVSS) */
    sources: z.object({ production: z.boolean(), scanner: z.boolean() }),
});
/** Detail nálezu = nález + jeho katalógový záznam + ostatné stránky s tou istou zraniteľnosťou. */
export const FindingDetailSchema = z.object({
    finding: FindingSchema,
    vulnerability: VulnerabilityDetailSchema,
    /** odporúčanie pre KOMPONENT tohto nálezu (VulnSoftware.remediation), nie prvé v zozname */
    remediation: z.string().nullable(),
    affectedSites: z.array(z.object({
        findingId: z.string(),
        siteId: z.string(),
        name: z.string(),
        host: z.string(),
        status: FindingStatusSchema,
        installedVersion: z.string(),
    })),
});
